/**
 * RESPONSIBILITY: pick the cell that draws one @List column, by its declared kind.
 *
 * A lookup and not a `switch`, for the reason the renderer registry exists: teaching the
 * list a new column is adding a key here. Anything with no entry is text — the same
 * default the annotations use.
 *
 * The plain input cells live here because they are one line each. The two that own
 * something — an upload, a vocabulary read from outside the row — have their own files.
 *
 * None of them draws its own name: the row does that, from the annotation. The
 * `placeholder` here is a hint and nothing more — it was standing in for a label, and a
 * placeholder is gone the moment the field has a value, which is why a filled row read as
 * a stack of anonymous boxes. An image or a <select> could never carry one at all.
 */
import { str, type Cell, type ItemField } from './cell-types';
import { imageCell } from './cell-image';
import { videoCell } from './cell-video';
import { lookupCell } from './cell-lookup';
import { colorCell } from './cell-color';
import { stringListCell } from './cell-stringlist';
import { choiceGroupCell } from './cell-choice-group';
import { listCell } from './cell-list';
import { repeatCell } from './cell-repeat';

export type { Cell, CellProps, ItemField } from './cell-types';

/**
 * A width the editor can actually type into.
 *
 * Every cell used to be `flex: 1 1 120px` regardless of what it held, so a field declared
 * `maxLength: 600` was edited 120 pixels at a time. The annotation already says how long
 * the value can be, so that is what decides.
 */
const roomFor = (f: ItemField): string =>
  f.maxLength && f.maxLength <= 40 ? `${f.maxLength + 6}ch` : '100%';

const TextCell: Cell['render'] = ({ field, value, onChange, id }) => (
  <input
    id={id} className="ctl-input" placeholder={field.label} maxLength={field.maxLength}
    value={str(value)} onChange={(e) => onChange(e.target.value)}
  />
);

const ParagraphCell: Cell['render'] = ({ field, value, onChange, id }) => (
  <textarea
    id={id} className="ctl-input" placeholder={field.label} maxLength={field.maxLength}
    rows={field.rows ?? 3} value={str(value)} onChange={(e) => onChange(e.target.value)}
  />
);

const LinkCell: Cell['render'] = ({ field, value, onChange, id }) => (
  <input
    id={id} type="url" className="ctl-input" placeholder={field.label}
    value={str(value)} onChange={(e) => onChange(e.target.value)}
  />
);

/** Driven by the schema rather than the kind: any column with an enum is a fixed choice. */
const ChoiceCell: Cell['render'] = ({ value, onChange, id, enumVals = [] }) => (
  <select id={id} className="ctl-input" value={str(value)} onChange={(e) => onChange(e.target.value)}>
    {enumVals.map((v) => <option key={v} value={v}>{v}</option>)}
  </select>
);

// <input type=date> wants YYYY-MM-DD; datetime-local wants YYYY-MM-DDTHH:mm.
const DateCell: Cell['render'] = ({ field, value, onChange, id }) => (
  <input
    id={id} type={field.time ? 'datetime-local' : 'date'} className="ctl-input"
    value={str(value).slice(0, field.time ? 16 : 10)}
    onChange={(e) => onChange(e.target.value)}
  />
);

/** Empty means empty, not zero — an untouched number field must not save as 0. */
const NumCell: Cell['render'] = ({ field, value, onChange, id }) => (
  <input
    id={id} type="number" className="ctl-input" placeholder={field.label}
    min={field.min} max={field.max} step={field.integer ? 1 : 'any'}
    value={value === undefined || value === null ? '' : String(value)}
    onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
  />
);

const BoolCell: Cell['render'] = ({ value, onChange, id }) => (
  <input
    id={id} type="checkbox" className="ctl-cell-bool"
    checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)}
  />
);

const TEXT: Cell = { render: TextCell, basis: roomFor };
const CHOICE: Cell = { render: ChoiceCell, basis: () => '16ch' };

/**
 * Every kind an annotation can declare needs an entry, or it falls through to text —
 * and a fallback cannot warn. @Color looked merely wrong that way; @StringList was worse,
 * rendering an array as "a,b,c" and saving the string back over it.
 *
 * `list` draws a nested repeatable — see cell-list.tsx; it was absent while "a list inside
 * a row" was still a design question.
 */
const CELLS: Record<string, Cell> = {
  paragraph:  { render: ParagraphCell, basis: () => '100%' },
  link:       { render: LinkCell,      basis: () => '34ch' },
  date:       { render: DateCell,      basis: (f) => (f.time ? '21ch' : '16ch') },
  num:        { render: NumCell,       basis: () => '11ch' },
  bool:       { render: BoolCell,      basis: () => 'auto', grow: 0 },
  img:        imageCell,
  video:      videoCell,
  color:      colorCell,
  stringlist: stringListCell,
  list:       listCell,
  repeat:     repeatCell,
};

export const cellFor = (field: ItemField, enumVals?: string[]): Cell => {
  // Asked for native inputs, so neither the select nor the chips below apply — and it is
  // checked before the enum, or a fixed vocabulary would never reach it.
  if (field.kind === 'choice' && field.as) return choiceGroupCell;
  if (enumVals) return CHOICE;
  // A choice with no enum in the schema is one whose values live in the document.
  if (field.kind === 'choice') return lookupCell;
  return CELLS[field.kind] ?? TEXT;
};
